import { Box, Dialog, IconButton } from "@mui/material";
import Image from "next/image";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useAlertStore } from "@/components/context/alert-store";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Link from "@/components/core/localized-link";
import { useSession } from "next-auth/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import React, { useState } from "react";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";
import LoginToSaveProduct from "@/components/core/alert-messages/login-warning";
import { useMutation } from "@tanstack/react-query";
import { sendGAEventTrigger } from "@/utils/analytics-trigger";
import { postRequest } from "@/utils/request";
import { IProduct } from "@/types/product";
import ImageWithFallback from "@/components/core/image-with-fallback";

interface ProductProps {
  product: IProduct;
  lang: string;
}

const ImageSection = ({ product, lang }: ProductProps) => {
  const { setMessage, showUnexpectedErrorMessage, showTooManyAttemptsMessage } = useAlertStore();
  const { status } = useSession();

  const images = product.images;

  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState<number>(0);
  const cookieKey = status === "unauthenticated" ? "anon-wishlist" : "wishlist";
  const wishlistRaw = Cookies.get(cookieKey);
  let wishlistArray: string[] = [];

  if (wishlistRaw) {
    try {
      wishlistArray = JSON.parse(wishlistRaw);
    } catch {
      wishlistArray = [];
    }
  }
  const productId = product.id.toString();

  const addedToWishlist = wishlistArray.some((wishlistItem: string) => wishlistItem === productId);
  const handleNext = () => {
    setPhotoIndex((prevIndex) => (prevIndex + 1) % product.images.length);
  };

  const handlePrev = () => {
    setPhotoIndex((prevIndex) => (prevIndex === 0 ? product.images.length - 1 : prevIndex - 1));
  };

  const addToWishlistMutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "wishlist", id: String(product.id) },
        body: {},
        lang,
      }),
    onSuccess: (data) => {
      if (data.success === 0) {
        if (data.tooManyAttempts) {
          showTooManyAttemptsMessage();
          return;
        }
        setMessage({
          text: data.error[0],
          severity: "error",
        });
        return;
      }
      setMessage({
        text: (
          <Trans>
            Success: You have added{" "}
            <Link href={`/product/${product.seo_url}`} color="black">
              {product.name}
            </Link>{" "}
            to your{" "}
            <Link href={"/account/wishlist"} color="black">
              wishlist
            </Link>
            !
          </Trans>
        ),
        severity: "success",
      });
    },
    onError: () => {
      showUnexpectedErrorMessage();
    },
  });
  const removeFromWishlistMutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "wishlist", id: String(product.id) },
        body: {},
        method: "DELETE",
        lang,
      }),
    onSuccess: () => {
      setMessage({
        text: (
          <Trans>
            Success: You have deleted{" "}
            <Link href={`/product/${product.seo_url}`} color="black">
              {product.name}
            </Link>{" "}
            from your{" "}
            <Link href={"/account/wishlist"} color="black">
              wishlist
            </Link>
            !
          </Trans>
        ),
        severity: "success",
      });
    },
    onError: () => {
      showUnexpectedErrorMessage();
    },
  });

  const handleWishlistClick = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (status === "unauthenticated") {
      setMessage({
        text: <LoginToSaveProduct item={{ id: productId, name: product.name }} />,
        severity: "warning",
      });
    }
    const addedToWishlist = wishlistArray.some((wishlistItem: any) => wishlistItem === productId);

    if (addedToWishlist) {
      if (status !== "unauthenticated") {
        removeFromWishlistMutation.mutate();
      }

      wishlistArray = wishlistArray.filter(function (item: string) {
        return item !== productId;
      });
    } else {
      if (status !== "unauthenticated") {
        addToWishlistMutation.mutate();
      }

      sendGAEventTrigger("Add To Wishlist", {
        send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
        label: product.name,
        category: "ecommerce",
      });
      wishlistArray.push(productId);
    }
    if (status === "unauthenticated") {
      Cookies.set("anon-wishlist", JSON.stringify(wishlistArray));
    } else {
      Cookies.set("wishlist", JSON.stringify(wishlistArray));
    }
  };

  return (
    <>
      <Box
        component="section"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
        }}
        data-testid="image-section"
      >
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "absolute",
            left: 0,
            top: "10px",
          }}
          data-testid="icon-button-container"
        >
          <IconButton
            onClick={handleWishlistClick}
            data-testid="product-wishlist"
            disableRipple
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "100%",
              bgcolor: "rgb(246, 246, 247)",
              width: "50px",
              height: "50px",
              minWidth: "auto",
              color: addedToWishlist ? "primary.main" : "#000",
              "&:hover": {
                bgcolor: "primary.main",
                color: "#fff",
              },
            }}
          >
            {addedToWishlist ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        </Box>
        <ImageWithFallback
          className="m-[75px] !max-h-[unset] rounded-xl object-contain md:!max-h-[unset]"
          fallBackImageClassName="min-h-[150px] object-contain md:!min-h-[300px] p-[60px]"
          src={product.image}
          alt={t`Giftcard image`}
          width={435}
          height={100}
          data-testid="product-image"
          sizes="(max-width: 768px) 50vw, 435px"
        />
      </Box>

      {product.images.length > 0 && (
        <Swiper
          slidesPerView={3}
          spaceBetween={2}
          breakpoints={{
            768: { slidesPerView: 3, spaceBetween: 20 },
            1024: { slidesPerView: 4, spaceBetween: 20 },
          }}
          modules={[Pagination]}
          className="-mt-[50px] h-fit w-full"
        >
          {product.images.map((image: string, index: number) => (
            <SwiperSlide key={index} className="flex items-center justify-center">
              <Image
                priority
                src={image}
                width={128}
                height={128}
                alt={`Product Image ${index + 1}`}
                className="block h-auto max-w-full cursor-pointer"
                onClick={() => {
                  setPhotoIndex(index);
                  setIsOpen(true);
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        sx={{ "&.MuiPaper-root-MuiDialog-paper": { maxWidth: "430px" } }}
      >
        <div className="relative flex h-full items-center justify-center">
          <IconButton
            disableFocusRipple
            disableRipple
            aria-label="prev"
            onClick={handlePrev}
            sx={{
              position: "fixed",
              left: { xs: 0, md: 25 },
              zIndex: 100,
              color: "#000",
            }}
          >
            <ArrowLeftIcon sx={{ fontSize: "90px" }} />
          </IconButton>
          <div className="relative">
            <IconButton
              disableFocusRipple
              disableRipple
              aria-label="close"
              onClick={() => setIsOpen(false)}
              sx={{
                position: "absolute",
                right: 0,
                width: "fit-content",
              }}
            >
              <CloseIcon sx={{ fontSize: "30px" }} />
            </IconButton>
            <Image
              priority
              src={images[photoIndex]}
              width={800}
              height={800}
              alt={`Product Image ${photoIndex + 1}`}
              className="w-fit"
              sizes="(max-width: 768px) 50vw, 800px"
            />
          </div>

          <IconButton
            disableFocusRipple
            disableRipple
            aria-label="next"
            onClick={handleNext}
            sx={{
              position: "fixed",
              right: { xs: 0, md: 25 },
              zIndex: 100,
              color: "#000",
            }}
          >
            <ArrowRightIcon sx={{ fontSize: "90px" }} />
          </IconButton>
        </div>
      </Dialog>
    </>
  );
};

export default React.memo(ImageSection);
